"use client";

import { useEffect, useRef, useState } from "react";

export const ZOOM_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

/**
 * ZoomImage — three-source zoom viewer with smooth zoom-out transitions.
 *
 * Each level loads its own pre-cropped image (server-side):
 *   level 1 → tightUrl  (tightest crop, smallest payload)
 *   level 2 → mediumUrl (wider crop)
 *   level 3 → fullUrl   (the original photo — only loaded on reveal)
 *
 * Two motivations are baked in:
 *   1. The full image — which lets a player right-click and identify
 *      the car — is never sent to the browser until they reach L3
 *      (game complete or final guess). L1 and L2 see only the cropped
 *      regions.
 *   2. Level transitions feel like a continuous zoom-out, not a
 *      crossfade. When advancing levels, the new (wider-source) image
 *      mounts on top of the old at a transform that makes it look
 *      pixel-for-pixel like the old level's view. Then it animates to
 *      its natural transform, "zooming out" to reveal more context.
 *      The old layer stays mounted underneath only as a safety net
 *      (the new layer is opaque) and unmounts after the transition.
 *
 * Geometry: tight & medium crops are centered on (centerX, centerY)
 * with radii tightRadius / mediumRadius (fractions of the source).
 * That means scaling the medium crop by mR/tR around its own center
 * shows the same region the tight crop natively shows. The full image
 * isn't pre-cropped, so aligning it to a tighter view requires a
 * scale around (centerX, centerY) plus a translate to put that point
 * at container center.
 *
 * Reverse direction (zooming back in) snaps because we can't
 * synthesize a wider crop from a tighter image — the tighter file
 * doesn't contain the surrounding pixels. In practice the only
 * reverse path in prod is the GameBoard reveal effect, which sets
 * transitionMs=0 for the snap-to-tight before the animated zoom-out.
 */
export function ZoomImage({
  tightUrl,
  mediumUrl,
  fullUrl,
  level,
  centerX,
  centerY,
  tightRadius,
  mediumRadius,
  transitionMs,
  easing = ZOOM_EASING,
}: {
  tightUrl: string;
  mediumUrl: string;
  fullUrl: string;
  level: number;
  centerX: number;
  centerY: number;
  tightRadius: number;
  mediumRadius: number;
  transitionMs: number;
  easing?: string;
}) {
  const urlForLevel = (lv: number) =>
    lv === 1 ? tightUrl : lv === 2 ? mediumUrl : fullUrl;
  const targetUrl = urlForLevel(level);

  const counterRef = useRef(0);
  const prevLevelRef = useRef(level);
  const [layers, setLayers] = useState<
    Array<{ url: string; key: number; level: number; fromLevel: number }>
  >(() => [{ url: targetUrl, key: 0, level, fromLevel: level }]);

  useEffect(() => {
    const top = layers[layers.length - 1];
    if (top.level === level && top.url === targetUrl) {
      prevLevelRef.current = level;
      return;
    }
    let cancelled = false;
    const fromLevel = prevLevelRef.current;
    prevLevelRef.current = level;
    const preload = new Image();
    preload.src = targetUrl;
    const promote = () => {
      if (cancelled) return;
      counterRef.current += 1;
      const key = counterRef.current;
      setLayers((prev) => [
        ...prev,
        { url: targetUrl, key, level, fromLevel },
      ]);
      // Drop everything below the new top after the transition settles.
      // +60ms slack covers the rAF gap that delays the transform commit.
      window.setTimeout(() => {
        if (!cancelled) {
          setLayers((prev) => prev.filter((l) => l.key === key));
        }
      }, transitionMs + 60);
    };
    if (preload.complete && preload.naturalWidth > 0) {
      promote();
    } else {
      preload.onload = promote;
      // Fall through on error so the UI doesn't get stuck on the prior level.
      preload.onerror = promote;
    }
    return () => {
      cancelled = true;
    };
    // layers intentionally omitted: the effect should re-run only when the
    // requested URL/level changes, not when the stack mutates from inside it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUrl, level, transitionMs]);

  // Speculative preload of the next zoom level so the next swap is instant.
  useEffect(() => {
    const next = level === 1 ? mediumUrl : level === 2 ? fullUrl : null;
    if (!next) return;
    const img = new Image();
    img.src = next;
  }, [level, mediumUrl, fullUrl]);

  return (
    <>
      {layers.map((layer, idx) => {
        const plan = transformPlan(
          layer.level,
          layer.fromLevel,
          centerX,
          centerY,
          tightRadius,
          mediumRadius,
        );
        const animate =
          idx === layers.length - 1 &&
          layers.length > 1 &&
          layer.fromLevel !== layer.level &&
          transitionMs > 0;
        return (
          <ZoomLayer
            key={layer.key}
            url={layer.url}
            transformOrigin={plan.transformOrigin}
            startTransform={plan.startTransform}
            endTransform={plan.endTransform}
            transitionMs={transitionMs}
            easing={easing}
            animate={animate}
          />
        );
      })}
    </>
  );
}

/** Plan a layer's transforms.
 *
 *  Returns a constant `transformOrigin` (kept fixed for the layer's
 *  whole life — see note below) and the start/end `transform` values.
 *
 *  Why one origin: CSS only animates `transform`, not `transform-origin`.
 *  If origin changed between start and end, it would snap to the end
 *  value at the start of the animation, which makes the start frame
 *  render with the wrong pivot — producing a visible jump. So we pick
 *  ONE origin per layer (the one the start transform needs) and use
 *  `transform: none` at the end. Identity renders the same regardless
 *  of origin, so this is safe.
 *
 *  layerLevel === fromLevel: layer is mounting at its natural state, no
 *  animation. Origin can be anything.
 *
 *  Going to a tighter view (fromLevel > layerLevel): can't synthesize a
 *  wider crop from a tighter image, so falls through to identity. The
 *  GameBoard reveal effect handles this by setting transitionMs=0 for
 *  the snap-to-tight before the animated zoom-out. */
function transformPlan(
  layerLevel: number,
  fromLevel: number,
  cx: number,
  cy: number,
  tR: number,
  mR: number,
): { transformOrigin: string; startTransform: string; endTransform: string } {
  // Natural mount — no animation, no transforms.
  if (layerLevel === fromLevel) {
    return {
      transformOrigin: "50% 50%",
      startTransform: "none",
      endTransform: "none",
    };
  }
  // Medium crop appearing as tight: crops 1 and 2 are both pre-centered
  // on the answer, so scaling around the image center is enough. Both
  // start and end share origin '50% 50%'; transform animates from
  // scale(mR/tR) to none.
  if (layerLevel === 2 && fromLevel === 1) {
    return {
      transformOrigin: "50% 50%",
      startTransform: `scale(${mR / tR})`,
      endTransform: "none",
    };
  }
  // Full image appearing as medium or tight: scale around the answer
  // point so it stays fixed, then translate so that point lands at
  // container center. End state is the full image's natural display
  // (transform: none), with the same origin — origin doesn't affect
  // identity rendering, so the answer naturally returns to (cx, cy)
  // of the container.
  if (layerLevel === 3 && (fromLevel === 1 || fromLevel === 2)) {
    const r = fromLevel === 1 ? tR : mR;
    const s = 1 / (2 * r);
    return {
      transformOrigin: `${cx * 100}% ${cy * 100}%`,
      startTransform: `translate(${(0.5 - cx) * 100}%, ${(0.5 - cy) * 100}%) scale(${s})`,
      endTransform: "none",
    };
  }
  // Going wider (fromLevel < layerLevel): can't synthesize. Snap.
  return {
    transformOrigin: "50% 50%",
    startTransform: "none",
    endTransform: "none",
  };
}

function ZoomLayer({
  url,
  transformOrigin,
  startTransform,
  endTransform,
  transitionMs,
  easing,
  animate,
}: {
  url: string;
  transformOrigin: string;
  startTransform: string;
  endTransform: string;
  transitionMs: number;
  easing: string;
  animate: boolean;
}) {
  // Mount with the start transform; on the next frame swap to the end
  // transform. The CSS transition does the in-between work. Double rAF
  // ensures the start commit lands in the DOM before the swap, so the
  // browser actually observes the change and runs the transition.
  // transform-origin stays constant — see transformPlan() comment for
  // why interpolating origin would break the start frame.
  const [t, setT] = useState<string>(animate ? startTransform : endTransform);
  useEffect(() => {
    if (!animate) return;
    let outer = 0;
    let inner = 0;
    outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setT(endTransform));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
    // endTransform is stable for the lifetime of the layer; only re-run
    // when the animate flag flips (i.e. on mount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animate]);

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={url}
      alt=""
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        transformOrigin,
        transform: t,
        transitionProperty: "transform",
        transitionDuration: `${transitionMs}ms`,
        transitionTimingFunction: easing,
        pointerEvents: "none",
        willChange: "transform",
      }}
    />
  );
}
